import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Lock, Loader2, LogOut, Calendar, DollarSign, ClipboardList,
  Phone, Mail, MapPin, PawPrint, ChevronDown,
} from "lucide-react";
import { toast } from "sonner";
import LogoWordmark from "@/components/Logo";
import { Input } from "@/components/ui/input";
import { fetchAdminBookings } from "@/lib/api";

const TOKEN_KEY = "dp_admin_token";

const fmtDate = (iso) => {
  try {
    return new Date(iso + "T00:00:00").toLocaleDateString("en-US", {
      weekday: "short", month: "short", day: "numeric",
    });
  } catch {
    return iso;
  }
};

const timeToMin = (t) => {
  const m = /(\d+):(\d+)\s*(AM|PM)/i.exec(t || "");
  if (!m) return 0;
  let h = parseInt(m[1], 10) % 12;
  if (m[3].toUpperCase() === "PM") h += 12;
  return h * 60 + parseInt(m[2], 10);
};

const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const BookingCard = ({ b, i, dimmed }) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: Math.min(i * 0.05, 0.4), duration: 0.4 }}
    className={`rounded-2xl border border-white/10 bg-[#111827] p-5 ${dimmed ? "opacity-45" : ""}`}
    data-testid={`admin-booking-card-${i}`}
  >
    <div className="flex items-start justify-between gap-3">
      <div>
        <div className="text-[#7CB342] font-bold text-sm tracking-wide">
          {fmtDate(b.date)} · {b.time}
        </div>
        <div className="mt-1 font-display font-bold text-lg text-[#F5F1E8]">{b.full_name}</div>
        <div className="text-sm text-[#9CA3AF]">
          {b.service_name} · {String(b.vehicle_size || "").replace(/^./, (c) => c.toUpperCase())}
        </div>
      </div>
      <div className="text-right">
        <div className="font-display font-extrabold text-xl text-[#7CB342]">${Number(b.price || 0).toFixed(0)}</div>
        {b.pet_hair && (
          <span className="mt-1 inline-flex items-center gap-1 text-[11px] font-bold text-[#E53935]">
            <PawPrint size={12} /> PET HAIR
          </span>
        )}
      </div>
    </div>
    <div className="mt-4 space-y-1.5 text-sm">
      <a href={`tel:${b.phone}`} className="flex items-center gap-2 text-[#F5F1E8]/80 hover:text-[#7CB342]">
        <Phone size={14} className="text-[#2EA9E0]" /> {b.phone}
      </a>
      <a href={`mailto:${b.email}`} className="flex items-center gap-2 text-[#F5F1E8]/80 hover:text-[#7CB342] break-all">
        <Mail size={14} className="text-[#2EA9E0] shrink-0" /> {b.email}
      </a>
      <a
        href={`https://maps.google.com/?q=${encodeURIComponent(b.address || "")}`}
        target="_blank"
        rel="noreferrer"
        className="flex items-center gap-2 text-[#F5F1E8]/80 hover:text-[#7CB342]"
      >
        <MapPin size={14} className="text-[#2EA9E0] shrink-0" /> {b.address}
      </a>
      {b.notes && <div className="pt-1 text-xs text-[#9CA3AF] italic">"{b.notes}"</div>}
    </div>
  </motion.div>
);

export default function AdminPage() {
  const [token, setToken] = useState(sessionStorage.getItem(TOKEN_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [showPast, setShowPast] = useState(false);

  const load = async (t) => {
    setLoading(true);
    try {
      const data = await fetchAdminBookings(t);
      setBookings(data);
      setAuthed(true);
      sessionStorage.setItem(TOKEN_KEY, t);
    } catch {
      setAuthed(false);
      sessionStorage.removeItem(TOKEN_KEY);
      toast.error("Wrong access code — try again");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) load(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { upcoming, past, weekRevenue } = useMemo(() => {
    const today = todayIso();
    const sorted = [...bookings].sort(
      (a, b) => a.date.localeCompare(b.date) || timeToMin(a.time) - timeToMin(b.time)
    );
    const upcoming = sorted.filter((b) => b.date >= today && b.status !== "cancelled");
    const past = sorted.filter((b) => b.date < today || b.status === "cancelled").reverse();
    const weekEnd = new Date();
    weekEnd.setDate(weekEnd.getDate() + 7);
    const we = `${weekEnd.getFullYear()}-${String(weekEnd.getMonth() + 1).padStart(2, "0")}-${String(weekEnd.getDate()).padStart(2, "0")}`;
    const weekRevenue = upcoming
      .filter((b) => b.date < we)
      .reduce((s, b) => s + Number(b.price || 0), 0);
    return { upcoming, past, weekRevenue };
  }, [bookings]);

  const signOut = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setAuthed(false);
    setToken("");
    setBookings([]);
  };

  return (
    <div className="min-h-[100svh] bg-[#0A0E17]" data-testid="admin-page">
      <div className="mx-auto w-full max-w-3xl px-4 sm:px-6 pt-8 pb-20">
        <div className="flex items-center justify-between">
          <Link to="/" data-testid="admin-logo"><LogoWordmark /></Link>
          {authed && (
            <button
              onClick={signOut}
              className="inline-flex items-center gap-1.5 text-sm text-[#9CA3AF] hover:text-[#E53935] transition-colors"
              data-testid="admin-signout-btn"
            >
              <LogOut size={15} /> Sign out
            </button>
          )}
        </div>

        {!authed ? (
          <motion.form
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            onSubmit={(e) => { e.preventDefault(); load(input); }}
            className="mt-14 mx-auto max-w-sm bg-[#111827] rounded-3xl border border-white/10 p-8 text-center"
            data-testid="admin-login-form"
          >
            <div className="w-14 h-14 mx-auto rounded-full bg-[#7CB342]/15 border border-[#7CB342]/40 flex items-center justify-center mb-5">
              <Lock size={22} className="text-[#7CB342]" />
            </div>
            <h1 className="font-display font-black text-2xl text-[#F5F1E8]">Owner Access</h1>
            <p className="mt-2 text-sm text-[#9CA3AF]">Enter your admin code to see the appointment book.</p>
            <Input
              type="password"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Admin code"
              data-testid="admin-token-input"
              className="mt-6 bg-[#0A0E17] border-white/10 focus-visible:border-[#7CB342] focus-visible:ring-[#7CB342]/30 text-[#F5F1E8] text-center"
            />
            <button
              type="submit"
              disabled={loading || input.length < 3}
              className="btn-pickle mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full py-3.5 text-sm disabled:opacity-60"
              data-testid="admin-login-btn"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : <Lock size={16} />}
              {loading ? "Checking..." : "Unlock"}
            </button>
          </motion.form>
        ) : (
          <div className="mt-10">
            <div className="label-eyebrow mb-2">Appointment Book</div>
            <h1 className="font-display font-black text-3xl sm:text-4xl text-[#F5F1E8] tracking-tight">
              Your <span className="text-[#7CB342] italic">schedule</span>
            </h1>

            <div className="mt-7 grid grid-cols-3 gap-3" data-testid="admin-stats">
              <div className="rounded-2xl border border-white/10 bg-[#111827] p-4 text-center">
                <Calendar size={18} className="mx-auto text-[#2EA9E0] mb-1.5" />
                <div className="font-display font-extrabold text-2xl text-[#F5F1E8]" data-testid="admin-stat-upcoming">{upcoming.length}</div>
                <div className="text-[10px] tracking-[0.15em] uppercase text-[#9CA3AF] font-bold">Upcoming</div>
              </div>
              <div className="rounded-2xl border border-white/10 bg-[#111827] p-4 text-center">
                <DollarSign size={18} className="mx-auto text-[#7CB342] mb-1.5" />
                <div className="font-display font-extrabold text-2xl text-[#F5F1E8]" data-testid="admin-stat-revenue">${weekRevenue.toFixed(0)}</div>
                <div className="text-[10px] tracking-[0.15em] uppercase text-[#9CA3AF] font-bold">Next 7 days</div>
              </div>
              <div className="rounded-2xl border border-white/10 bg-[#111827] p-4 text-center">
                <ClipboardList size={18} className="mx-auto text-[#9CA3AF] mb-1.5" />
                <div className="font-display font-extrabold text-2xl text-[#F5F1E8]" data-testid="admin-stat-total">{bookings.length}</div>
                <div className="text-[10px] tracking-[0.15em] uppercase text-[#9CA3AF] font-bold">All time</div>
              </div>
            </div>

            <h2 className="mt-9 mb-4 font-display font-bold text-xl text-[#F5F1E8]">Upcoming</h2>
            {upcoming.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-white/15 p-8 text-center text-sm text-[#9CA3AF]" data-testid="admin-empty-upcoming">
                No appointments coming up. Share your booking link or QR card to fill the calendar.
              </div>
            ) : (
              <div className="space-y-4" data-testid="admin-upcoming-list">
                {upcoming.map((b, i) => <BookingCard key={b.id || i} b={b} i={i} dimmed={false} />)}
              </div>
            )}

            {past.length > 0 && (
              <>
                <button
                  onClick={() => setShowPast((v) => !v)}
                  className="mt-9 mb-4 inline-flex items-center gap-2 font-display font-bold text-xl text-[#9CA3AF] hover:text-[#F5F1E8] transition-colors"
                  data-testid="admin-past-toggle"
                >
                  Past ({past.length})
                  <ChevronDown size={18} className={`transition-transform ${showPast ? "rotate-180" : ""}`} />
                </button>
                {showPast && (
                  <div className="space-y-4" data-testid="admin-past-list">
                    {past.map((b, i) => <BookingCard key={b.id || i} b={b} i={i} dimmed />)}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
